import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { 
  User, Mail, Star, Trash2, LogOut, Settings, 
  BookOpen, Calendar, Shield, Trophy, Download 
} from 'lucide-react';
import SEO from '../components/SEO';
import LoadingScreen from '../components/LoadingScreen'; // <--- IMPORT STANDARD

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [myNotes, setMyNotes] = useState([]);
  
  // Stato per l'installazione PWA
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  
  const navigate = useNavigate();

  // 1. Gestione Installazione App (PWA)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Impedisce che il browser mostri il banner standard brutto
      e.preventDefault();
      // Salva l'evento per attivarlo dopo col nostro tasto bello
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  // 2. Caricamento Dati Utente
  useEffect(() => {
    const getProfileData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          navigate('/login');
          return;
        }
        setUser(user);

        // Prendi i dati del profilo
        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        setProfile(profileData);

        // Prendi le dispense caricate dall'utente
        const { data: notesData } = await supabase
          .from('notes')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        setMyNotes(notesData || []);

      } catch (error) {
        console.error('Errore caricamento profilo:', error);
      } finally {
        setLoading(false);
      }
    };

    getProfileData();
  }, [navigate]);

  // 3. Funzione per eliminare una dispensa E IL FILE DAL BUCKET
  const handleDeleteNote = async (noteId, fileUrl) => {
    if (!window.confirm("Sei sicuro? Eliminando il file perderai i 50 punti guadagnati.")) return;

    try {
      // 1. TENTIAMO DI CANCELLARE IL FILE DALLO STORAGE
      if (fileUrl) {
        // Il fileUrl è tipo: https://.../storage/v1/object/public/appunti/USER_ID/NOME_FILE.pdf
        // A noi serve solo la parte relativa: USER_ID/NOME_FILE.pdf
        
        // Questo trucco prende tutto ciò che c'è dopo l'ultimo segmento "appunti/"
        const fileUrlParts = fileUrl.split('/appunti/'); 

        if (fileUrlParts.length > 1) {
            const filePath = fileUrlParts[1];
            const { error: storageError } = await supabase.storage
                .from('appunti') // Assicurati che il nome del bucket sia giusto (dallo screen è 'appunti')
                .remove([filePath]);
            
            if (storageError) {
                console.error("Errore cancellazione file storage:", storageError);
                // Non blocchiamo: se fallisce lo storage, proviamo comunque a pulire il DB
            }
        }
      }

      // 2. CANCELLIAMO LA RIGA DAL DATABASE
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      // 3. AGGIORNIAMO L'INTERFACCIA
      setMyNotes(myNotes.filter(n => n.id !== noteId));
      
      if (profile) {
        setProfile({ ...profile, points: Math.max(0, profile.points - 50) });
      }
      
      alert("Dispensa eliminata e punti aggiornati.");
    } catch (error) {
      console.error("Errore cancellazione:", error);
      alert("Errore durante l'eliminazione.");
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  // --- LOADING SCREEN STANDARD ---
  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20 px-4 font-sans text-slate-800">
      <SEO title="Il mio Profilo" description="Gestisci il tuo account TusciaHub." url="/profile" />
      
      <div className="max-w-5xl mx-auto space-y-6">
        
        {/* --- CARD UTENTE & PUNTI --- */}
        <div className="grid md:grid-cols-3 gap-6">
            {/* Profilo */}
            <div className="md:col-span-2 bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100 flex flex-col sm:flex-row items-center sm:items-start gap-6 relative overflow-hidden">
                <div className="w-24 h-24 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-4xl font-black shrink-0 shadow-inner">
                    {user.email.charAt(0).toUpperCase()}
                </div>
                <div className="text-center sm:text-left z-10">
                    <h1 className="text-3xl font-black text-slate-900 mb-2">
                        Ciao, {profile?.username || user.email.split('@')[0]}! 👋
                    </h1>
                    <div className="flex flex-col gap-1 text-slate-500 text-sm font-medium">
                        <span className="flex items-center justify-center sm:justify-start gap-2"><Mail size={14}/> {user.email}</span>
                        <span className="flex items-center justify-center sm:justify-start gap-2"><Calendar size={14}/> Iscritto dal {new Date(user.created_at).toLocaleDateString('it-IT')}</span>
                    </div>
                </div>
                {/* Decorazione sfondo */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-indigo-50 to-white rounded-full blur-3xl -z-0 opacity-50 pointer-events-none"></div>
            </div>

            {/* Punti (Gamification) */}
            <div className="bg-gradient-to-br from-yellow-400 to-orange-500 p-8 rounded-[2rem] shadow-lg text-white flex flex-col justify-between relative overflow-hidden group hover:scale-[1.02] transition-transform">
                <div className="absolute top-[-20%] right-[-20%] text-white/20 rotate-12 group-hover:rotate-45 transition-transform duration-700">
                    <Star size={150} />
                </div>
                <div>
                    <p className="font-bold text-yellow-100 text-sm uppercase tracking-widest mb-1">Il tuo Punteggio</p>
                    <div className="text-5xl font-black flex items-center gap-2">
                        {profile?.points || 0} <span className="text-2xl opacity-80">pt</span>
                    </div>
                </div>
                <div className="mt-6 flex items-center gap-2 text-sm font-medium bg-white/20 w-fit px-3 py-1.5 rounded-full backdrop-blur-sm">
                    <Trophy size={16} /> Livello: {profile?.points > 500 ? 'Veterano 🎓' : profile?.points > 100 ? 'Studente Attivo 📚' : 'Matricola 🌱'}
                </div>
            </div>
        </div>

        {/* --- I MIEI FILE CARICATI --- */}
        <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
            <h2 className="text-2xl font-black text-slate-900 mb-6 flex items-center gap-3">
                <BookOpen className="text-indigo-600"/> Le tue Dispense
            </h2>

            {myNotes.length > 0 ? (
                <div className="grid gap-4">
                    {myNotes.map(note => (
                        <div key={note.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-5 rounded-2xl bg-slate-50 border border-slate-100 hover:border-indigo-200 transition group">
                            <div className="mb-4 sm:mb-0">
                                <h3 className="font-bold text-slate-800 text-lg mb-1">{note.title}</h3>
                                <p className="text-sm text-slate-500 line-clamp-1">{note.description || "Nessuna descrizione"}</p>
                                <div className="flex gap-3 mt-2 text-xs font-bold text-slate-400 uppercase">
                                    <span className="bg-white px-2 py-1 rounded border border-slate-200">{new Date(note.created_at).toLocaleDateString()}</span>
                                    <span className="bg-indigo-50 text-indigo-500 px-2 py-1 rounded border border-indigo-100">+50 Punti</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Link to={`/note/${note.id}`} className="px-4 py-2 bg-white text-slate-600 border border-slate-200 rounded-xl text-sm font-bold hover:bg-slate-50 transition shadow-sm">
                                    Vedi
                                </Link>
                                <button 
                                    onClick={() => handleDeleteNote(note.id, note.file_url)} // Passiamo anche il file_url
                                    className="px-4 py-2 bg-red-50 text-red-500 border border-red-100 rounded-xl text-sm font-bold hover:bg-red-100 transition flex items-center gap-2"
                                    title="Elimina e perdi punti"
                                >
                                    <Trash2 size={16}/> Elimina
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-12 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                    <div className="bg-indigo-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-indigo-400">
                        <UploadIcon size={32} />
                    </div>
                    <p className="text-slate-500 font-medium mb-4">Non hai ancora caricato nulla.</p>
                    <Link to="/dipartimenti" className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-700 transition shadow-lg shadow-indigo-200">
                        Carica la prima dispensa (+50 pt)
                    </Link>
                </div>
            )}
        </div>

        {/* --- TASTO INSTALLA APP (SOLO SE DISPONIBILE) --- */}
        {deferredPrompt && (
            <button onClick={handleInstallClick} className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 p-6 rounded-[2rem] shadow-lg hover:shadow-xl transition flex items-center justify-between group text-white animate-in slide-in-from-bottom-4">
                <div className="flex items-center gap-4">
                    <div className="bg-white/20 p-3 rounded-2xl text-white group-hover:scale-110 transition">
                        <Download size={24} />
                    </div>
                    <div className="text-left">
                        <h3 className="font-bold text-lg">Installa l'App</h3>
                        <p className="text-xs text-indigo-100">Scarica TusciaHub sul telefono per un accesso rapido</p>
                    </div>
                </div>
                <div className="bg-white text-indigo-600 px-4 py-2 rounded-xl text-sm font-bold shadow-sm">
                    Installa
                </div>
            </button>
        )}

        {/* --- IMPOSTAZIONI --- */}
        <div className="grid md:grid-cols-2 gap-6">
            <Link to="/update-password" className="bg-white p-6 rounded-[2rem] border border-slate-100 hover:shadow-md transition flex items-center gap-4 group">
                <div className="bg-slate-100 p-3 rounded-2xl text-slate-600 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition">
                    <Settings size={24} />
                </div>
                <div>
                    <h3 className="font-bold text-slate-900">Sicurezza Account</h3>
                    <p className="text-xs text-slate-500">Modifica la tua password</p>
                </div>
            </Link>

            <button onClick={handleLogout} className="bg-white p-6 rounded-[2rem] border border-slate-100 hover:shadow-md transition flex items-center gap-4 group text-left w-full">
                <div className="bg-red-50 p-3 rounded-2xl text-red-500 group-hover:bg-red-100 transition">
                    <LogOut size={24} />
                </div>
                <div>
                    <h3 className="font-bold text-slate-900">Disconnettiti</h3>
                    <p className="text-xs text-slate-500">Esci dal tuo account</p>
                </div>
            </button>
        </div>

      </div>
    </div>
  );
};

// Icona helper per il pulsante upload
const UploadIcon = ({size}) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
);

export default Profile;